import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Input,
  Checkbox,
  Table,
  Tbody,
  useToast,
  Tr,
  Td,
  Th,
  Thead,
  Flex,
  FormControl,
  Select,
  Box,
  Button,
  HStack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import "./quicklinkgroups.css";
import { useMsal } from "@azure/msal-react";
import { useAppContext } from "../../../context/AppContext";
import {
  fetchQuickLinksMapping,
  updateQuickLinksGroupMapping,
} from "../../../services/AdminServices/QuickLinksMapping";

interface LinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: number | null;
  quickLinks: any[];
  onSave: (updatedLinks: any[]) => void;
}

const LinkModal: React.FC<LinkModalProps> = ({
  isOpen,
  onClose,
  groupId,
  quickLinks,
  onSave,
}) => {
  const [links, setLinks] = useState<any[]>([]); // Links fetched from API
  const [allChecked, setAllChecked] = useState<boolean>(false); // Header checkbox state
  const [selectedLinks, setSelectedLinks] = useState<number[]>([]); // State for selected links

  // api integration
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast(); // To show toasts

  // tenant id
  const { accounts } = useMsal();
  const { accessToken, userData } = useAppContext();
  const tenantID= userData?.tenantID ||0 ;

  const [searchQuery, setSearchQuery] = useState<string>(""); // State for search query
  const [currentPage, setCurrentPage] = useState(1); // Track the current page
  const [itemsPerPage, setItemsPerPage] = useState(5); // Items per page

  // Toggle the selection of a link
  const handleToggleLink = (quickLinksID: number) => {
    setSelectedLinks(
      (prev) =>
        prev.includes(quickLinksID)
          ? prev.filter((id) => id !== quickLinksID) // Uncheck the link
          : [...prev, quickLinksID] // Check the link
    );
  };

  // Toggle all links across all pages
  const handleToggleAll = () => {
    const allLinkIDs = filteredLinks.map((link) => link.quickLinksID);

    if (selectedLinks.length === filteredLinks.length) {
      // If all links are selected, uncheck all
      setSelectedLinks([]);
    } else {
      // If not all links are selected, check all filtered links
      setSelectedLinks(allLinkIDs);
    }
    setAllChecked((prev) => !prev); // Toggle the "Select All" checkbox state
  };

  const handleSave = async () => {
    
    if (!accessToken) {
      toast({
        position: "top",
        title: "Authentication Error",
        description: "Access token is missing. Please log in again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Prepare the selected link details
      const selectedLinkDetails = links.filter((link) =>
        selectedLinks.includes(link.quickLinksID)
      );
      console.log("Selected links are:", selectedLinkDetails);

      // Extract only the quickLinksID values
      const quickLinksIds = selectedLinkDetails.map(
        (link) => link.quickLinksID
      );

      // Call the service to update quick links group mapping
      await updateQuickLinksGroupMapping(
        accessToken,
        groupId!,
        quickLinksIds ,// Pass only the IDs
        tenantID
      );

      console.log("All quick links group mappings added successfully");
      onSave(selectedLinkDetails); // Update local state
      onClose(); // Close modal
    } catch (error) {
      console.error("Error occurred during API call:", error);
      setError("An error occurred while saving quick links.");
    }
  };

  const getQuickLinkMapping = async () => {
    try {
      if (accessToken) {
        // Fetch the quick links mapping data
        const data: {
          quickLinksID: number;
          name: string;
          url?: string;
          isChecked: boolean;
        }[] = await fetchQuickLinksMapping(accessToken, groupId || 0, tenantID);
        console.log("Quick Links Mapping Data:", data);

        // Sort links so that checked ones come first
        const sortedLinks = data.sort(
          (a: { isChecked: boolean }, b: { isChecked: boolean }) =>
            Number(b.isChecked) - Number(a.isChecked)
        );

        setLinks(sortedLinks); // Store the sorted data into state

        // Initialize selected links based on the isChecked value
        const initiallySelected = sortedLinks
          .filter((link) => link.isChecked) // Filter links that are already checked
          .map((link) => link.quickLinksID); // Extract the quickLinksID of the selected links

        setSelectedLinks(initiallySelected); // Set the selectedLinks state with the initial selections
      } else {
        setError("Access token is missing.");
      }
    } catch (error) {
      setError("Failed to fetch quick links.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      getQuickLinkMapping(); // Fetch data when accessToken or groupId changes
    }
  }, [accessToken, groupId, isOpen]); // Trigger when accessToken, groupId, or modal visibility changes

  useEffect(() => {
    // Reset to first page when search term changes
    setCurrentPage(1);
  }, [searchQuery]);

  // Close the modal and reset state
  const handleClose = () => {
    setSearchQuery(""); // Reset search query
    setCurrentPage(1); // Reset pagination
    setAllChecked(false); // Reset the "Check All" state
    onClose(); // Close the modal
  };

  // Change items per page
  const handleItemsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1); // Reset to the first page when items per page changes
  };

  // Change page
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Handle search input change
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearchQuery(e.target.value);

  // Filter links based on search query
  const filteredLinks = links.filter((link) =>
    link.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination logic
  const totalSites = filteredLinks.length;
  const totalPages = Math.ceil(totalSites / itemsPerPage); // Calculate total pages
  const indexOfLastSite = currentPage * itemsPerPage;
  const indexOfFirstSite = indexOfLastSite - itemsPerPage;
  const currentSites = filteredLinks.slice(indexOfFirstSite, indexOfLastSite); // Slice the filtered sites

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size={"xl"}>
      <ModalOverlay />
      <ModalContent maxWidth="900px" height="auto">
        <ModalHeader>Select Quick Links</ModalHeader>
        <ModalCloseButton />
        <hr style={{ border: "1px solid #black", margin: "10px" }} />
        <Flex justify="space-between" align="center" p={4}>
          {/* Items per page selector */}
          <Select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            maxWidth="180px"
          >
            <option value={5}>5 items per page</option>
            <option value={6}>6 items per page</option>
          </Select>

          {/* Search input */}
          <FormControl maxWidth="300px">
            <Input
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search links..."
            />
          </FormControl>
        </Flex>

        <ModalBody>
          <Table mt={0} className="Table">
            <Thead>
              <Tr>
                <Th width="50px">
                  <Checkbox
                    isChecked={
                      selectedLinks.length === links.length && links.length > 0
                    } // Check all if all are selected
                    onChange={handleToggleAll}
                    isIndeterminate={
                      selectedLinks.length > 0 &&
                      selectedLinks.length < links.length
                    } // Indeterminate state if some are selected
                  />
                </Th>
                <Th>Title</Th>
                <Th>URL</Th>
              </Tr>
            </Thead>
            <Tbody>
              {currentSites.map((link) => (
                <Tr key={link.quickLinksID}>
                  <Td style={{ wordBreak: "break-word", whiteSpace: "normal", maxWidth: "150px" }}>
                    <Checkbox
                      isChecked={selectedLinks.includes(link.quickLinksID)} // Check if link is in selectedLinks
                      onChange={() => handleToggleLink(link.quickLinksID)} // Toggle link selection
                    />
                  </Td>
                  <Td>{link.name}</Td>
                  <Td style={{ wordBreak: "break-word", whiteSpace: "normal", maxWidth: "150px" }}>{link.url || "No URL provided"}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>

          {/* Pagination Info */}
          <Flex justify="space-between" mt={4}>
            <Box>
              Showing {Math.min(totalSites, indexOfFirstSite + 1)} to{" "}
              {Math.min(indexOfLastSite, totalSites)} of {totalSites} entries
            </Box>

            {/* Pagination Controls */}
            <HStack>
              <Button
                onClick={() => handlePageChange(currentPage - 1)}
                isDisabled={currentPage === 1}
              >
                Previous
              </Button>
              <Select
                value={currentPage}
                onChange={(e) => handlePageChange(Number(e.target.value))}
                maxWidth="70px"
              >
                {Array.from({ length: totalPages }, (_, index) => (
                  <option key={index} value={index + 1}>
                    {index + 1}
                  </option>
                ))}
              </Select>
              <Button
                onClick={() => handlePageChange(currentPage + 1)}
                isDisabled={currentPage === totalPages}
              >
                Next
              </Button>
            </HStack>
          </Flex>
          <hr style={{ border: "1px solid #black", margin: "10px" }} />
          {/* Save and Close Buttons */}
          <Flex justify="flex-end" mt={3}>
            <Button onClick={handleSave} colorScheme="blue" mr={3}>
              Save
            </Button>
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default LinkModal;
